// GPS-012 capability probes — the ONLY branch the shell introduces
// (ADR-0004, driver-app-spec §5.10: "capability presence is the only
// branch — never user-agent or 'is native'"). Each probe asks whether a
// concrete capability is present on the page: the injected Capacitor
// bridge registers native plugins, and their presence IS the capability.
//
// Deliberately import-free: this module is reachable from the eager boot
// path (app.ts) and from TripModePage, and the Capacitor JS runtime must
// never enter those bundles (GPS-012.6 — the pure PWA ships unchanged;
// shellNative.ts is the lazy chunk that actually imports it). In a
// browser, `window.Capacitor` does not exist and every probe is false.

interface CapacitorBridge {
  isPluginAvailable?: (name: string) => boolean;
}

function bridge(): CapacitorBridge | null {
  const cap = (window as Window & { Capacitor?: CapacitorBridge }).Capacitor;
  return cap && typeof cap.isPluginAvailable === 'function' ? cap : null;
}

/** Background geolocation is available — the shell's tracking tier. */
export function shellGeoCapable(): boolean {
  return bridge()?.isPluginAvailable?.('BackgroundGeolocation') === true;
}

/**
 * The OS can hand this page a launch/deep-link URL (the Capacitor App
 * plugin is registered) — the shell's entry tier.
 */
export function shellEntryCapable(): boolean {
  return bridge()?.isPluginAvailable?.('App') === true;
}

/**
 * The DRV11 install-prompt targets, from the boot-time meta stamps
 * (SHELL_INSTALL_URL_ANDROID / SHELL_INSTALL_URL_IOS via cmd/server/
 * spa.go — a session-less page cannot read the bearer-gated /api/config).
 * Empty when unset (OI-007 pending): the prompt stays dark — honest
 * absence, never a dead button.
 */
export function shellInstallUrls(): { android?: string; ios?: string } {
  const read = (name: string): string | undefined => {
    const v = document
      .querySelector(`meta[name="${name}"]`)
      ?.getAttribute('content')
      ?.trim();
    return v && /^https:\/\//.test(v) ? v : undefined;
  };
  const android = read('fexo-shell-install-android');
  const ios = read('fexo-shell-install-ios');
  return { ...(android ? { android } : {}), ...(ios ? { ios } : {}) };
}
